/**
 * Copyright (c) 2016-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  LocalStateError,
  ServerError,
  ServerParseError,
  type TypePolicies,
  UnconventionalError,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { ErrorLink } from "@apollo/client/link/error";
import { uniqBy } from "@ndla/util";
import config from "../config";
import type { LocaleType } from "../interfaces";
import { NOT_FOUND, UNAUTHORIZED } from "../statusCodes";
import { getActiveSessionCookieClient, getFeideCookie, invalidateSession, isActiveSession } from "./authHelpers";
import {
  NDLAGraphQLError,
  ApolloNetworkError,
  ApolloLocalStateError,
  ApolloServerParseError,
  ApolloUnconventionalError,
} from "./error/NDLAApolloErrors";
import { handleError } from "./handleError";

export const apiBaseUrl = config.runtimeType === "test" ? "http://ndla-api" : config.ndlaApiUrl;

const getGraphqlUri = (): string => {
  if (config.localGraphQLApi) return "http://localhost:4000/graphql-api/graphql";
  if (!config.isClient && config.graphqlApiHost) return `http://${config.graphqlApiHost}/graphql-api/graphql`;
  return `${apiBaseUrl}/graphql-api/graphql`;
};

const uri = getGraphqlUri();

export function apiResourceUrl(path: string) {
  return apiBaseUrl + path;
}

const possibleTypes = {
  TaxonomyEntity: ["Resource", "Topic"],
  SearchResult: ["ArticleSearchResult", "LearningpathSearchResult", "NodeSearchResult"],
  BaseLearningpath: ["MyNdlaLearningpath", "Learningpath"],
  BaseLearningpathStep: ["MyNdlaLearningpathStep", "LearningpathStep"],
  MyNdlaResourceMeta: [
    "MyNdlaArticleResourceMeta",
    "MyNdlaAudioResourceMeta",
    "MyNdlaConceptResourceMeta",
    "MyNdlaImageResourceMeta",
    "MyNdlaLearningpathResourceMeta",
    "MyNdlaTopicResourceMeta",
    "MyNdlaVideoResourceMeta",
  ],
};

const typePolicies: TypePolicies = {
  Folder: {
    fields: {
      resources: {
        merge(existing, incoming) {
          const allResources = [...(existing ?? []), ...(incoming ?? [])];
          return uniqBy(allResources, (resource) => resource.__ref);
        },
      },
    },
  },
  SubjectPage: {
    fields: {
      about: {
        merge(existing, incoming) {
          return existing ? existing : incoming;
        },
      },
    },
  },
  Article: {
    keyFields: (_, opts) => (opts.readField("revision") ? ["id", "revision"] : ["id"]),
  },
  SearchContext: {
    keyFields: ["contextId"],
  },
  SearchResult: {
    keyFields: ["id", "__typename"],
  },
  FrontpageMenu: {
    keyFields: ["articleId"],
  },
  TaxonomyContext: {
    keyFields: ["contextId"],
  },
  MyNdlaResourceMeta: {
    keyFields: ["type", "id"],
  },
  MyNdlaResourceMetaSearch: {
    keyFields: ["type", "id"],
  },
  MyNdlaResourceConnection: {
    keyFields: ["resourceId", "folderId"],
  },
  ConfigMetaBoolean: {
    keyFields: ["key"],
  },
  ConfigMetaStringList: {
    keyFields: ["key"],
  },
};

function getCache() {
  const cache: InMemoryCache = new InMemoryCache({
    possibleTypes,
    typePolicies,
  });
  if (config.isClient) {
    cache.restore(window.DATA.apolloState);
  }

  return cache;
}

export const createApolloClient = (language: LocaleType, versionHash?: any, signal?: AbortSignal) => {
  const cache = getCache();

  return new ApolloClient({
    link: createApolloLinks(language, versionHash, signal),
    cache,
    ssrMode: !config.isClient,
    defaultOptions: {
      watchQuery: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
      // Suspense query refs are only retained in effects, so on the server they live until auto-disposed
      // (30s by default), keeping the per-request client and its cache in memory. Release them quickly.
      react: config.isClient ? undefined : { suspense: { autoDisposeTimeoutMs: 1_000 } },
    },
  });
};

export const createApolloLinks = (lang: LocaleType, versionHash?: any, signal?: AbortSignal) => {
  const versionHeader: Record<string, string> = versionHash ? { versionHash: versionHash } : {};

  const headers = {
    "Accept-Language": lang,
    ...versionHeader,
  };

  const authLink = new ApolloLink((operation, forward) => {
    if (config.isClient) {
      const idToken = getFeideCookie(document.cookie);
      if (idToken && isActiveSession(getActiveSessionCookieClient())) {
        operation.setContext(({ headers = {} }) => ({
          headers: { ...headers, FeideAuthorization: `Bearer ${idToken}` },
        }));
      }
    }
    return forward(operation);
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    // Requests are aborted on purpose when the render they belong to is abandoned, so there is nothing to log.
    if (signal?.aborted) return;
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((err) => {
        if (config.isClient && err.extensions?.status === UNAUTHORIZED) {
          invalidateSession(operation.client);
        } else if (err.extensions?.status !== NOT_FOUND) {
          handleError(new NDLAGraphQLError(err, operation));
        }
      });
    } else if (ServerError.is(error)) {
      if (config.isClient && error.statusCode === UNAUTHORIZED) {
        invalidateSession(operation.client);
      } else {
        handleError(new ApolloNetworkError(error, operation));
      }
    } else if (LocalStateError.is(error)) {
      handleError(new ApolloLocalStateError(error, operation));
    } else if (ServerParseError.is(error)) {
      handleError(new ApolloServerParseError(error, operation));
    } else if (UnconventionalError.is(error)) {
      handleError(new ApolloUnconventionalError(error, operation));
      // This is either a CombinedProtocolError or a non-graphql error somehow. We don't need any special handling for any of them.
    } else {
      handleError(error, { operationName: operation.operationName });
    }
  });

  return ApolloLink.from([
    errorLink,
    authLink,
    typeof navigator !== "undefined" && navigator.webdriver
      ? new HttpLink({ uri, headers, fetchOptions: { signal } })
      : new BatchHttpLink({ uri, headers, fetchOptions: { signal } }),
  ]);
};
